import { SecondSignaturePolicy } from '@features/accountsSettings/components/emarSettings/types'
import useEmarSecondSignatureSettings from '@features/accountsSettings/components/emarSettings/useEmarSecondSignatureSettings'
import { Button, FormCard, FormItem } from '@lmc/ui-kit'
import { Text } from '@lmc/ui-kit/src/general'
import { Form, Select } from 'antd'
import useEligibleSecondSigners from 'hooks/useEligibleSecondSigners'
import useFeatureFlags from 'hooks/useFeatureFlags'
import { NAMESPACE } from 'locale'
import { emarV2Constants } from 'locale/emarV2'
import { forwardRef, useImperativeHandle } from 'react'
import { useTranslation } from 'react-i18next'
import styled from 'styled-components'

const EMAR_SECOND_SIGNATURE_FLAG = 'emar_add_edit_medication_second_signature'
const SECOND_SIGNER_FIELD = 'secondSigner'

export type EmarSecondSignatureSectionHandle = {
  validate: () => Promise<boolean>
  getSecondSignerId: () => string | undefined
  getSecondSignerName: () => string | undefined
}

type EmarSecondSignatureSectionProps = {
  isControlledDrug: boolean
  testId?: string
}

const getSectionCopy = (
  policy: SecondSignaturePolicy,
  isControlledDrug: boolean
) => {
  if (policy === SecondSignaturePolicy.RequiredForAllMedication) {
    return {
      titleKey: emarV2Constants.SECOND_SIGNATURE_SECTION_TITLE,
      helperKey: emarV2Constants.SECOND_SIGNATURE_SECTION_HELPER_ALL,
      required: true
    }
  }

  if (policy === SecondSignaturePolicy.RequiredForControlledDrugsOnly) {
    return isControlledDrug
      ? {
          titleKey: emarV2Constants.SECOND_SIGNATURE_SECTION_TITLE,
          helperKey: emarV2Constants.SECOND_SIGNATURE_SECTION_HELPER_CONTROLLED,
          required: true
        }
      : {
          titleKey: emarV2Constants.SECOND_SIGNATURE_SECTION_TITLE_OPTIONAL,
          helperKey:
            emarV2Constants.SECOND_SIGNATURE_SECTION_HELPER_CONTROLLED_OPTIONAL,
          required: false
        }
  }

  return {
    titleKey: emarV2Constants.SECOND_SIGNATURE_SECTION_TITLE_OPTIONAL,
    helperKey: emarV2Constants.SECOND_SIGNATURE_SECTION_HELPER_OPTIONAL,
    required: false
  }
}

const EmarSecondSignatureSection = forwardRef<
  EmarSecondSignatureSectionHandle,
  EmarSecondSignatureSectionProps
>(({ isControlledDrug, testId }, ref) => {
  const { t } = useTranslation(NAMESPACE.EMAR_V2)
  const [form] = Form.useForm()

  const { data: flags, isLoading: isFlagsLoading } = useFeatureFlags({
    flagNames: [EMAR_SECOND_SIGNATURE_FLAG]
  })
  const isFeatureEnabled = flags?.[EMAR_SECOND_SIGNATURE_FLAG]?.enabled

  const {
    requirement,
    isLoading: isSettingLoading,
    isError: isSettingError,
    refetch: refetchSettings
  } = useEmarSecondSignatureSettings({ enabled: isFeatureEnabled })

  const { options, isLoading: isSignersLoading } = useEligibleSecondSigners({
    enabled: isFeatureEnabled
  })

  const isReady =
    Boolean(isFeatureEnabled) && !isSettingLoading && !isSettingError

  useImperativeHandle(
    ref,
    () => ({
      validate: async () => {
        if (!isFeatureEnabled) {
          return true
        }
        // The real policy is unknown, so don't fall back to treating it as
        // optional — block the save until it loads or the retry succeeds.
        if (isSettingError) {
          return false
        }
        if (!isReady) {
          return true
        }
        try {
          await form.validateFields()
          return true
        } catch {
          // validateFields() is called imperatively, so antd's scrollToFirstError (tied to form.submit()) never fires — scroll/focus by hand.
          form.scrollToField(SECOND_SIGNER_FIELD, {
            behavior: 'smooth',
            block: 'center'
          })
          // preventScroll: the browser's own instant focus-scroll would otherwise cut the smooth scroll above short.
          form.getFieldInstance(SECOND_SIGNER_FIELD)?.focus({
            preventScroll: true
          })
          return false
        }
      },
      getSecondSignerId: () =>
        isReady ? form.getFieldValue(SECOND_SIGNER_FIELD) : undefined,
      getSecondSignerName: () => {
        if (!isReady) {
          return undefined
        }
        const signerId = form.getFieldValue(SECOND_SIGNER_FIELD)
        return options.find((option) => option.value === signerId)?.label
      }
    }),
    [isFeatureEnabled, isSettingError, isReady, form, options]
  )

  if (isFlagsLoading || !isFeatureEnabled) {
    return null
  }

  if (isSettingError) {
    return (
      <FormCard
        title={t(emarV2Constants.SECOND_SIGNATURE_SECTION_TITLE)}
        testId={testId}
      >
        <Text>{t(emarV2Constants.SECOND_SIGNATURE_LOAD_ERROR)}</Text>
        <Button variant="default" onClick={() => refetchSettings()}>
          {t(emarV2Constants.SECOND_SIGNATURE_RETRY)}
        </Button>
      </FormCard>
    )
  }

  if (!isReady) {
    return null
  }

  const { titleKey, helperKey, required } = getSectionCopy(
    requirement,
    isControlledDrug
  )

  return (
    <FormCard
      title={t(titleKey)}
      subtitle={t(helperKey)}
      level={2}
      testId={testId}
    >
      <Form form={form} layout="vertical">
        <FormItem shouldUpdate noStyle>
          {() => {
            const hasError = form.getFieldError(SECOND_SIGNER_FIELD).length > 0

            return (
              <FormItem
                name={SECOND_SIGNER_FIELD}
                label={
                  <Text>
                    {t(emarV2Constants.SECOND_SIGNATURE_SECTION_LABEL)}
                  </Text>
                }
                required={required}
                rules={
                  required
                    ? [
                        {
                          required: true,
                          message: t(
                            emarV2Constants.SECOND_SIGNATURE_SECTION_ERROR
                          )
                        }
                      ]
                    : []
                }
                extra={
                  !hasError && (
                    <Text level={2}>
                      {t(emarV2Constants.SECOND_SIGNATURE_SECTION_NOTE)}
                    </Text>
                  )
                }
              >
                <StyledSelect
                  placeholder={t(
                    emarV2Constants.SECOND_SIGNATURE_SECTION_PLACEHOLDER
                  )}
                  loading={isSignersLoading}
                  showSearch
                  filterOption={(input, option) =>
                    String(option?.label ?? '')
                      .toLowerCase()
                      .includes(input.toLowerCase())
                  }
                >
                  {options.map((option) => (
                    <Select.Option
                      key={option.value}
                      value={option.value}
                      label={option.label}
                    >
                      <OptionContainer>
                        <ImageContainer>{option.image}</ImageContainer>
                        <Text>{option.label}</Text>
                      </OptionContainer>
                    </Select.Option>
                  ))}
                </StyledSelect>
              </FormItem>
            )
          }}
        </FormItem>
      </Form>
    </FormCard>
  )
})

const StyledSelect = styled(Select)`
  width: 400px;
`

const OptionContainer = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
`

const ImageContainer = styled.div`
  display: flex;
  align-items: center;
`

export default EmarSecondSignatureSection
